import AlertOctagonFill from '@/assets/icons/alert_octagon_fill.svg?component';
import InformationLine from '@/assets/icons/information_line.svg?component';

import type { CustomerBannerMessageVariant } from './types';

/**
 * Keep values updated with the targeted pages defined in "targetedPages.ts" file in sanity project.
 */
const TARGETED_PAGES_TO_REGULAR_EXPRESSIONS_MAPPING: Record<string, RegExp> = {
    // Region level
    'region': /^\/c\/\d+\/?$/m,
    'region.lice_report': /^\/c\/\d+\/lice-report\/?$/m,

    // Location level
    'location': /^\/c\/\d+\/location\/\d+\/?$/m,
    'location.lice_report': /^\/c\/\d+\/location\/\d+\/lice-report\/?$/m,
    'location.compare': /^\/c\/\d+\/location\/\d+\/compare\/?$/m,
    'location.video': /^\/c\/\d+\/location\/\d+\/video\/?$/m,
    'location.winch': /^\/c\/\d+\/location\/\d+\/winch\/?$/m,

    // Pen level - Dashboard
    'pen': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/?$/m,
    'pen.dashboard.images': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/dashboard\/images\/?$/m,
    'pen.dashboard.graphs': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/dashboard\/graphs\/?$/m,
    'pen.dashboard.bioscope_status': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/dashboard\/bioscope\/?$/m,

    // Pen level - Weight
    'pen.weight': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/weight\/?$/m,
    'pen.weight.distribution': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/weight\/distribution\/?$/m,
    'pen.weight.growth': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/weight\/growth\/?$/m,
    'pen.weight.graphs': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/weight\/graphs\/?$/m,

    // Pen level - Lice
    'pen.lice': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/lice\/?$/m,
    'pen.lice.detections': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/lice\/detections\/?$/m,
    'pen.lice.adult_female': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/lice\/female\/?$/m,
    'pen.lice.mobile': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/lice\/mobile\/?$/m,
    'pen.lice.caligus': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/lice\/caligus\/?$/m,
    'pen.lice.graphs': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/lice\/graphs\/?$/m,
    'pen.lice.lice_report': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/lice\/lice-report\/?$/m,

    // Pen level - Welfare
    'pen.welfare': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/welfare\/?$/m,
    'pen.welfare.wounds_insight': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/welfare\/wounds\/?.*$/m,
    'pen.welfare.scale_loss': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/welfare\/scaleloss\/?$/m,
    'pen.welfare.maturation': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/welfare\/maturation\/?$/m,
    'pen.welfare.detections_maturation':
        /^\/c\/\d+\/location\/\d+\/pen\/\d+\/welfare\/detections\/?$/m,
    'pen.welfare.swim_speed': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/welfare\/speed\/?$/m,

    // Pen level - Health
    'pen.health': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/?$/m,
    'pen.health.body_wounds.laksvel':
        /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/body-wounds\/laksvel\/?$/m,
    'pen.health.body_wounds.insight':
        /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/body-wounds\/insight\/?$/m,
    'pen.health.body_wounds.graphs':
        /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/body-wounds\/graphs\/?$/m,
    'pen.health.snout_wounds': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/snout-wounds\/?$/m,
    'pen.health.maturation': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/maturation\/?$/m,
    'pen.health.scale_loss': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/scale-loss\/?$/m,
    'pen.health.skin_bleeding': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/skin-bleeding\/?$/m,
    'pen.health.fin_status': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/fin-status\/?$/m,
    'pen.health.emaciation': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/emaciation\/?$/m,
    'pen.health.deformities.spine':
        /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/deformities\/spine\/?$/m,
    'pen.health.deformities.jaw':
        /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/deformities\/jaw\/?$/m,
    'pen.health.deformities.gill_lid':
        /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/deformities\/gill-lid\/?$/m,
    'pen.health.cataract': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/cataract\/?$/m,
    'pen.health.eye_damage': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/health\/eye-damage\/?$/m,

    // Pen level - Behavior level
    'pen.behavior': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/behavior\/?$/m,
    'pen.behavior.swim_speed': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/behavior\/swim-speed\/?$/m,
    'pen.behavior.respiration': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/behavior\/respiration\/?$/m,
    'pen.behavior.tilt': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/behavior\/tilt\/?$/m,

    // Pen level - Other
    'pen.reports': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/reports\/?.*$/m,
    'pen.settings': /^\/c\/\d+\/location\/\d+\/pen\/\d+\/settings\/?$/m,

    // Common
    'move_bioscope': /^.*\/move-bioscope\/?$/m,
    'users': /^.*\/users\/?$/m,
    'help': /^.*\/help\/?$/m,
};

export function isCurrentPageInTargetedPage(currentPath: string, targetedPage: string) {
    const regularExpression = TARGETED_PAGES_TO_REGULAR_EXPRESSIONS_MAPPING[targetedPage] ?? null;
    return regularExpression?.test(currentPath) ?? false;
}

export function getCustomerBannerMessageIcon(variant: CustomerBannerMessageVariant) {
    switch (variant) {
        case 'informative':
            return InformationLine;
        case 'warning':
            return AlertOctagonFill;
        case 'neutral':
            return InformationLine;
        default:
            return InformationLine;
    }
}

export function makeLocalStorageKey(bannerId: string) {
    return `closed-banner-${bannerId}`;
}
